import { useParams, Link } from 'react-router-dom';
import { Heart, ShoppingBag, Truck, RotateCcw, Shield, Minus, Plus, Check, Star, Share2, ChevronRight } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { supabase } from '../supabaseClient';
import { useEffect, useState } from 'react';
import ProductCard from '../components/ProductCard';

const ProductDetail = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState('');
  const [showAddedMsg, setShowAddedMsg] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [activeTab, setActiveTab] = useState('details');
  const { addToCart } = useCart();
  const { isInWishlist, addToWishlist, removeFromWishlist } = useWishlist();

  useEffect(() => {
    const fetchProduct = async () => {
const { data, error } = await supabase
  .from('products')
  .select(`
    *,
    product_details (
      description, material, capacity,
      care_ritual, delivery, returns_replacement
    )
  `)
  .eq('id', id)
  .eq('is_live', true)
  .single();

      if (error) {
        console.error(error);
      } else {
        setProduct({
          ...data,
          details: data.product_details?.[0] || null,
          sizes: data.sizes || [],
          images: data.images || []
        });
      }

      setLoading(false);
    };

    fetchProduct();
  }, [id]);

  useEffect(() => {
    if (!product) return;

    const fetchRelated = async () => {
      const { data } = await supabase
        .from('products')
.select('*')
.eq('is_live', true)
.not('is_live', 'is', null)
.eq('category', product.category)
.eq('is_live', true)
.neq('id', product.id)
.limit(4);

      setRelatedProducts(data || []);
    };

    fetchRelated();
  }, [product]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#faf8f5] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-3 border-[#e8e0d5] border-t-[#ba8245] rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#8b7355] font-light tracking-wide">Loading masterpiece...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-[#faf8f5] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="w-24 h-24 bg-[#f0ebe4] rounded-full flex items-center justify-center mx-auto mb-6">
            <ShoppingBag className="w-10 h-10 text-[#ba8245] opacity-50" />
          </div>
          <h2 className="font-serif text-3xl text-[#2c2418] mb-4">Piece Not Found</h2>
          <p className="text-[#8b7355] mb-8 font-light">The artisan piece you're looking for seems to have wandered off.</p>
          <Link 
            to="/shop" 
            className="inline-flex items-center gap-2 px-8 py-3 bg-[#2c2418] text-white rounded-full hover:bg-[#ba8245] transition-all duration-300"
          >
            Return to Gallery <ChevronRight size={16} />
          </Link>
        </div>
      </div>
    );
  }

  const inWishlist = isInWishlist(product.id);

  const handleWishlist = () => {
    if (inWishlist) {
      removeFromWishlist(product.id);
    } else {
      addToWishlist(product);
    }
  };

  const handleAddToCart = () => {
    if (product.sizes?.length && !selectedSize) {
      alert('Please select a size');
      return;
    }

    addToCart(product, selectedSize, quantity);

    setShowAddedMsg(true);

    setTimeout(() => {
      setShowAddedMsg(false);
    }, 2000);
  };

  const tabs = [
    { id: 'details', label: 'Product Details' },
    { id: 'materials', label: 'Materials & Care' },
    { id: 'shipping', label: 'Shipping & Returns' }
  ];

  return (
    <>
      {/* Toast Message */}
      <div 
        className={`fixed bottom-8 right-8 bg-[#2c2418] text-white px-6 py-4 rounded-xl shadow-2xl flex items-center gap-3 z-50 transition-all duration-500 ${
          showAddedMsg ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
        }`}
      >
        <div className="w-8 h-8 bg-[#ba8245] rounded-full flex items-center justify-center">
          <Check size={16} className="text-white" />
        </div>
        <div>
          <p className="font-medium text-sm">Added to cart</p>
          <p className="text-xs text-[#c4b5a0]">{product.name}</p>
        </div>
      </div>

      <div className="min-h-screen bg-gradient-to-b from-[#faf8f5] to-white">
        {/* Breadcrumb */}
        <div className="max-w-7xl mx-auto px-6 pt-8">
          <div className="flex items-center gap-2 text-sm text-[#8b7355]">
            <Link to="/" className="hover:text-[#ba8245] transition-colors">Home</Link>
            <ChevronRight size={14} />
            <Link to="/shop" className="hover:text-[#ba8245] transition-colors">Shop</Link>
            <ChevronRight size={14} />
            <span className="text-[#2c2418] font-medium truncate">{product.name}</span>
          </div>
        </div>

        {/* Product Detail */}
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Image Gallery */}
            <div className="space-y-4">
              <div className="relative aspect-[16/9] rounded-2xl overflow-hidden bg-[#f5f2ed] shadow-xl group">
                <img
                  src={product.images?.[selectedImage] || 'https://via.placeholder.com/800x450'}
                  alt={product.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <button 
                  className="absolute top-4 right-4 p-2 bg-white/90 backdrop-blur rounded-full shadow-md hover:bg-white transition-all"
                  onClick={handleWishlist}
                >
                  <Heart size={18} className={inWishlist ? 'fill-[#ba8245] text-[#ba8245]' : 'text-[#2c2418]'} />
                </button>
              </div>
              
              {/* Thumbnail Strip */}
              <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin">
                {product.images?.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`relative flex-shrink-0 w-20 h-24 rounded-lg overflow-hidden transition-all duration-300 ${
                      selectedImage === index
                        ? 'ring-2 ring-[#ba8245] shadow-lg scale-95'
                        : 'ring-1 ring-[#e8e0d5] opacity-70 hover:opacity-100'
                    }`}
                  >
                    <img
                      src={image}
                      alt={`${product.name} view ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Product Info */}
            <div className="space-y-6">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-3 py-1 bg-[#f0ebe4] text-[#ba8245] text-xs tracking-wider rounded-full">
                    {product.fabric || "Artisan Collection"}
                  </span>
                  <div className="flex items-center gap-1">
                    <Star size={14} className="fill-[#d4af37] text-[#d4af37]" />
                    <Star size={14} className="fill-[#d4af37] text-[#d4af37]" />
                    <Star size={14} className="fill-[#d4af37] text-[#d4af37]" />
                    <Star size={14} className="fill-[#d4af37] text-[#d4af37]" />
                    <Star size={14} className="fill-[#e8e0d5] text-[#e8e0d5]" />
                    <span className="text-xs text-[#8b7355] ml-1">(24 reviews)</span>
                  </div>
                </div>
                <h1 className="font-serif text-3xl md:text-4xl text-[#2c2418] mb-4 leading-tight">
                  {product.name}
                </h1>
                <div className="flex items-baseline gap-3">
                  <p className="text-3xl font-light text-[#ba8245]">
                    ₹{product.price ? product.price.toLocaleString() : "0"}
                  </p>
                  {product.original_price && (
                    <p className="text-lg text-[#b0a088] line-through">
                      ₹{product.original_price.toLocaleString()}
                    </p>
                  )}
                </div>
              </div>

              <div className="h-px bg-gradient-to-r from-transparent via-[#e8e0d5] to-transparent"></div>

              <p className="text-[#5c4b38] leading-relaxed font-light text-lg">
                {product.details?.description || "Handcrafted with precision and care, this piece embodies timeless elegance and artisanal excellence."}
              </p>

              {/* Size Selection */}
              {product.sizes?.length > 0 && (
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <h3 className="text-sm uppercase tracking-wider text-[#2c2418] font-medium">
                      Select Size
                    </h3>
                    <button className="text-xs text-[#ba8245] hover:underline">Size Guide</button>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {product.sizes.map(size => (
                      <button
                        key={size}
                        onClick={() => setSelectedSize(size)}
                        className={`px-6 py-3 text-sm uppercase tracking-wider rounded-full transition-all duration-300 ${
                          selectedSize === size
                            ? 'bg-[#2c2418] text-white shadow-lg scale-105'
                            : 'bg-white border border-[#e8e0d5] text-[#5c4b38] hover:border-[#ba8245] hover:scale-105'
                        }`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Quantity & Actions */}
              <div className="space-y-6">
                <div className="flex items-center gap-6">
                  <div>
                    <h3 className="text-sm uppercase tracking-wider text-[#2c2418] mb-3 font-medium">
                      Quantity
                    </h3>
                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        className="w-10 h-10 border border-[#e8e0d5] rounded-full flex items-center justify-center hover:border-[#ba8245] hover:bg-[#faf8f5] transition-all"
                      >
                        <Minus size={16} />
                      </button>
                      <span className="text-lg font-medium w-8 text-center text-[#2c2418]">
                        {quantity}
                      </span>
                      <button
                        onClick={() => setQuantity(quantity + 1)}
                        className="w-10 h-10 border border-[#e8e0d5] rounded-full flex items-center justify-center hover:border-[#ba8245] hover:bg-[#faf8f5] transition-all"
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4">
                  <button
                    onClick={handleAddToCart}
                    className="flex-1 bg-gradient-to-r from-[#2c2418] to-[#3d3224] text-white px-8 py-4 rounded-full flex items-center justify-center gap-3 hover:from-[#ba8245] hover:to-[#a6713a] transition-all duration-300 shadow-lg hover:shadow-xl group"
                  >
                    <ShoppingBag size={20} className="group-hover:scale-110 transition-transform" />
                    <span className="font-medium tracking-wide">Add to Cart</span>
                  </button>
                  <button
                    onClick={handleWishlist}
                    className={`px-6 py-4 rounded-full transition-all duration-300 ${
                      inWishlist
                        ? 'bg-[#ba8245] text-white shadow-lg'
                        : 'bg-white text-[#2c2418] border border-[#e8e0d5] hover:border-[#ba8245] hover:shadow-md'
                    }`}
                  >
                    <Heart size={20} fill={inWishlist ? 'currentColor' : 'none'} />
                  </button>
                  <button className="px-6 py-4 rounded-full bg-white text-[#2c2418] border border-[#e8e0d5] hover:border-[#ba8245] hover:shadow-md transition-all">
                    <Share2 size={20} />
                  </button>
                </div>
              </div>

              {/* Features */}
              <div className="grid grid-cols-3 gap-4 pt-4 pb-6">
                <div className="text-center group cursor-pointer">
                  <div className="w-12 h-12 bg-[#faf8f5] rounded-full flex items-center justify-center mx-auto mb-2 group-hover:bg-[#f0ebe4] transition-colors">
                    <Truck className="text-[#ba8245]" size={22} />
                  </div>
                  <p className="text-xs text-[#5c4b38] font-medium">Luxury Packaging</p>
                  <p className="text-[10px] text-[#8b7355]">Elegant unboxing</p>
                </div>
                <div className="text-center group cursor-pointer">
                  <div className="w-12 h-12 bg-[#faf8f5] rounded-full flex items-center justify-center mx-auto mb-2 group-hover:bg-[#f0ebe4] transition-colors">
                    <RotateCcw className="text-[#ba8245]" size={22} />
                  </div>
                  <p className="text-xs text-[#5c4b38] font-medium">Easy Returns</p>
                  <p className="text-[10px] text-[#8b7355]">14 days return</p>
                </div>
                <div className="text-center group cursor-pointer">
                  <div className="w-12 h-12 bg-[#faf8f5] rounded-full flex items-center justify-center mx-auto mb-2 group-hover:bg-[#f0ebe4] transition-colors">
                    <Shield className="text-[#ba8245]" size={22} />
                  </div>
                  <p className="text-xs text-[#5c4b38] font-medium">Secure Payment</p>
                  <p className="text-[10px] text-[#8b7355]">100% protected</p>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs Section */}
          <div className="mt-20">
            <div className="border-b border-[#e8e0d5]">
              <div className="flex gap-8">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`pb-4 px-1 transition-all duration-300 relative ${
                      activeTab === tab.id
                        ? 'text-[#2c2418] font-medium'
                        : 'text-[#8b7355] hover:text-[#ba8245]'
                    }`}
                  >
                    {tab.label}
                    {activeTab === tab.id && (
                      <span className="absolute bottom-0 left-0 w-full h-0.5 bg-[#ba8245] rounded-full"></span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="py-8">
              {activeTab === 'details' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-4">
                    <h3 className="font-serif text-xl text-[#2c2418]">Specifications</h3>
                    <div className="space-y-3">
                      <div className="flex py-2 border-b border-[#f0ebe4]">
                        <span className="w-32 text-sm text-[#8b7355]">Material</span>
                        <span className="text-sm text-[#2c2418]">{product.details?.material || "Premium Quality"}</span>
                      </div>
                      {/* <div className="flex py-2 border-b border-[#f0ebe4]">
                        <span className="w-32 text-sm text-[#8b7355]">Dimensions</span>
                        <span className="text-sm text-[#2c2418]">{product.details?.dimensions || "Standard"}</span>
                      </div> */}
                      <div className="flex py-2 border-b border-[#f0ebe4]">
                        <span className="w-32 text-sm text-[#8b7355]">Capacity</span>
                        <span className="text-sm text-[#2c2418]">{product.details?.capacity || "N/A"}</span>
                      </div>
                      <div className="flex py-2 border-b border-[#f0ebe4]">
                        <span className="w-32 text-sm text-[#8b7355]">Weight</span>
                        <span className="text-sm text-[#2c2418]">{product.weight || "0.5 kg"}</span>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <h3 className="font-serif text-xl text-[#2c2418]">Artisan Story</h3>
                    <p className="text-[#5c4b38] leading-relaxed">
                      Each piece is meticulously handcrafted by skilled artisans, preserving traditional techniques 
                      passed down through generations. Every item tells a unique story of dedication and craftsmanship.
                    </p>
                    <div className="bg-[#faf8f5] p-4 rounded-xl mt-4">
                      <p className="text-sm text-[#ba8245] font-medium">✨ Ethically Sourced</p>
                      <p className="text-xs text-[#8b7355] mt-1">Made with sustainable materials and fair trade practices</p>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'materials' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="font-serif text-xl text-[#2c2418] mb-4">Materials</h3>
                    <p className="text-[#5c4b38] leading-relaxed">
                      {product.details?.material || "Crafted from premium quality materials sourced from trusted suppliers, ensuring durability and elegance."}
                    </p>
                  </div>
                  <div>
                    <h3 className="font-serif text-xl text-[#2c2418] mb-4">Care Instructions</h3>
                    <p className="text-[#5c4b38] leading-relaxed">
                      {product.details?.care_ritual || "Clean with a soft, dry cloth. Avoid exposure to direct sunlight, moisture, and harsh chemicals. Store in a cool, dry place when not in use."}
                    </p>
                  </div>
                </div>
              )}

              {activeTab === 'shipping' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="font-serif text-xl text-[#2c2418] mb-4">Shipping Information</h3>
                    <p className="text-[#5c4b38] leading-relaxed">
                      {product.details?.delivery || "Free shipping on all orders above ₹5000. Estimated delivery: 5-7 business days. International shipping available."}
                    </p>
                  </div>
                  <div>
                    <h3 className="font-serif text-xl text-[#2c2418] mb-4">Returns & Exchange</h3>
                    <p className="text-[#5c4b38] leading-relaxed">
                      {product.details?.returns_replacement || "14-day easy returns. Items must be unused and in original packaging. Exchange available for size issues."}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Related Products */}
          {relatedProducts.length > 0 && (
            <div className="mt-24 pt-8">
              <div className="text-center mb-12">
                <h2 className="font-serif text-3xl md:text-4xl text-[#2c2418] mb-3">
                  Discover More
                </h2>
                <div className="w-20 h-0.5 bg-gradient-to-r from-[#ba8245] to-transparent mx-auto"></div>
                <p className="text-[#8b7355] mt-4 max-w-md mx-auto">
                  Discover pieces that pair perfectly with your selection
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
                {relatedProducts.map(product => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ProductDetail;